import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { customerApi, apiError, deviceId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Brand } from "@/components/Brand";
import { LanguageSelector } from "@/components/LanguageSelector";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Turnstile } from "@/components/Turnstile";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { loginSuccess } = useAuth();
  const { t, te, lang } = useLanguage();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [pin, setPin] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const sendCode = async () => {
    if (!name.trim()) return toast.error(t("err_name"));
    if (phone.trim().length < 8) return toast.error(t("err_phone"));
    if (!address.trim()) return toast.error(t("err_address"));
    if (!/^\d{4}$/.test(pin)) return toast.error(t("err_pin4"));
    if (!token) return toast.error(t("complete_security_check"));
    setBusy(true);
    try {
      await customerApi.post("/auth/send-code", {
        phone: phone.trim(), purpose: "register", turnstile_token: token, device_id: deviceId(),
      });
      setStep(2);
      setCooldown(60);
      toast.success(t("toast_code_sent"));
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const register = async () => {
    if (!/^\d{6}$/.test(code)) return toast.error(t("err_code6"));
    setBusy(true);
    try {
      const { data } = await customerApi.post("/auth/register", {
        name: name.trim(), phone: phone.trim(), address: address.trim(), pin, code, language: lang,
      });
      loginSuccess(data.token, data.customer);
      toast.success(t("toast_account_created"));
      nav("/home");
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0 || !token) return;
    setBusy(true);
    try {
      await customerApi.post("/auth/send-code", {
        phone: phone.trim(), purpose: "register", turnstile_token: token, device_id: deviceId(),
      });
      setCooldown(60);
      toast.success(t("toast_code_sent"));
    } catch (e) {
      toast.error(te(apiError(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <LanguageSelector />
      <div className="max-w-md mx-auto px-6 py-8">
        <button data-testid="register-back-btn" onClick={() => (step === 2 ? setStep(1) : nav("/"))}
          className="flex items-center gap-2 text-primary font-medium mb-6">
          <ArrowLeft className="h-5 w-5" /> {t("back")}
        </button>
        <Brand subtitle={t("create_account")} />

        {step === 1 && (
          <div className="mt-8 space-y-5 animate-slide-up">
            <Field label={t("full_name")}>
              <input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                placeholder={t("full_name_ph")} className="input-lg" />
            </Field>
            <Field label={t("mobile_number")}>
              <input data-testid="register-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                inputMode="tel" placeholder={t("mobile_ph")} className="input-lg" />
              <p className="text-xs text-muted-foreground mt-1">{t("login_account_note")}</p>
            </Field>
            <Field label={t("job_address")}>
              <AddressAutocomplete value={address} onChange={setAddress} testId="register-address-input" />
            </Field>
            <Field label={t("create_pin")}>
              <input data-testid="register-pin-input" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                inputMode="numeric" type="password" placeholder="••••" className="input-lg tracking-[0.5em] text-center" />
            </Field>
            <Turnstile onToken={setToken} />
            <button data-testid="register-sendcode-btn" onClick={sendCode} disabled={busy || !token} className="btn-primary">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("send_code")}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="mt-8 space-y-5 animate-slide-up">
            <div className="flex items-center gap-3 rounded-2xl bg-secondary p-4">
              <ShieldCheck className="h-6 w-6 text-primary shrink-0" />
              <p className="text-sm text-secondary-foreground">{t("code_sent_to", { phone })}</p>
            </div>
            <Field label={t("verification_code")}>
              <input data-testid="register-code-input" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputMode="numeric" placeholder={t("code_ph")} className="input-lg tracking-[0.4em] text-center" />
            </Field>
            <button data-testid="register-verify-btn" onClick={register} disabled={busy} className="btn-primary">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("verify_create")}
            </button>
            <button data-testid="register-resend-btn" onClick={resend} disabled={busy || cooldown > 0}
              className="w-full text-center text-sm text-muted-foreground hover:text-primary disabled:opacity-60 transition-colors">
              {cooldown > 0 ? t("resend_in", { s: cooldown }) : t("resend_code")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-primary mb-2">{label}</label>
      {children}
    </div>
  );
}
